import { CheckCheck, PencilLine } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { NamePicker } from "@/components/NamePicker";
import { QuestionAnswer } from "@/components/QuestionAnswer";
import { useApp } from "@/lib/AppContext";
import { ApiError, type Contradiction, type PendingCorrection, type ReviewInsight, type UnnamedSpeaker } from "@/lib/api";
import { cn } from "@/lib/cn";
import { withDate } from "@/lib/format";
import { NEEDS_YOU_COPY, useNeedsYou, type NeedsYouCard, type NeedsYouKind } from "@/lib/needsYou";
import { personColour, personInitials } from "@/lib/personAvatar";
import { describeUngrounded } from "@/lib/ungrounded";
import { useAudioFragment } from "@/lib/useAudioFragment";
import { FragmentPlayer } from "@/simple/FragmentPlayer";

/**
 * UI-16's Needs you on a phone — the same merged stream, the same order, one card at a time.
 *
 * M12 built this screen over two of the four kinds and said out loud why the other two were
 * missing: nothing served a queue of unnamed voices or of undecided wording fixes across
 * every recording, and half of a merged queue built before the ordering rule existed would
 * be the half without the ordering. Both exist now (`lib/needsYou.ts`), so the phone app is
 * the simple-mode surface it was always meant to be rather than a subset of one.
 *
 * **One card at a time is the point, not a space saving.** Full mode lists everything
 * because a list is how somebody works through a backlog at a desk; this shows one because
 * reviewing on a phone happens in a lift, and a decision taken with a thumb should be about
 * one thing. Nothing is decided with *less* information than the desktop shows: every card
 * carries its source, a contradiction carries both statements in full, an ungrounded insight
 * still says so, and a voice still gets a fragment to listen to.
 */
export function PhoneNeedsYou() {
  const { cards, problem, answered, reload } = useNeedsYou();

  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState<"left" | "right" | null>(null);

  const queue = cards ?? [];
  const total = queue.length;
  // Wraps, so skipping past the last card comes back to the first rather than emptying the
  // screen — "decide later" means later, not never.
  const position = total ? ((index % total) + total) % total : 0;
  const current = queue[position];

  useEffect(() => {
    if (total > 0 && index >= total) setIndex(0);
  }, [index, total]);

  function drop(card: NeedsYouCard, direction: "left" | "right") {
    setLeaving(direction);

    // Long enough for the exit animation the handoff names, and a fixed number rather than
    // a transition-end listener because the card unmounts either way.
    window.setTimeout(() => {
      setLeaving(null);
      answered(card.id);
    }, 220);
  }

  if (cards === null) return <p className="p-4 text-[14px] text-[var(--text-muted)]">Loading…</p>;

  if (problem) {
    return (
      <p role="alert" className="p-4 text-[14px] text-[var(--warning)]">
        {problem}
      </p>
    );
  }

  if (!current) return <AllClear />;

  const skip = () => setIndex(position + 1);

  return (
    <div
      key={current.id}
      className={cn(
        "flex min-h-full flex-col gap-3.5 p-4",
        leaving === "right" && "[animation:omExitRight_220ms_cubic-bezier(0.4,0,0.2,1)_both]",
        leaving === "left" && "[animation:omExitLeft_220ms_cubic-bezier(0.4,0,0.2,1)_both]",
      )}
    >
      <div className="flex items-center gap-2">
        <KindBadge kind={current.kind} />
        <span className="ml-auto text-[12px] text-[var(--text-muted)]">
          {position + 1} of {total}
        </span>
      </div>

      <p className="text-[13px] text-[var(--text-muted)] [text-wrap:pretty]">
        {COPY[current.kind].lead} · {sourceOf(current)}
      </p>

      {current.kind === "insight" && (
        <InsightCard insight={current.insight} onDecided={(way) => drop(current, way)} onSkip={skip} />
      )}
      {current.kind === "correction" && (
        <CorrectionCard correction={current.correction} onDecided={(way) => drop(current, way)} onSkip={skip} />
      )}
      {current.kind === "contradiction" && (
        <ContradictionCard card={current.contradiction} onAnswered={() => drop(current, "right")} onSkip={skip} />
      )}
      {current.kind === "speaker" && (
        <SpeakerCard speaker={current.speaker} onNamed={() => drop(current, "right")} onSkip={skip} />
      )}
      {current.kind === "question" && (
        <QuestionAnswer
          question={current.question}
          scale="phone"
          onAnswered={() => {
            drop(current, "right");
            // An answer can close a sibling card the Server's sweep knows about and this screen does not.
            window.setTimeout(reload, 260);
          }}
          onSkip={skip}
        />
      )}
    </div>
  );
}

/** The handoff's copy, per card kind — one set of words for both reduced surfaces. */
const COPY = NEEDS_YOU_COPY;

function sourceOf(card: NeedsYouCard): string {
  switch (card.kind) {
    case "correction":
      return withDate(card.correction.sourceTitle, card.correction.sourceDate);
    case "speaker":
      return withDate(card.speaker.sourceTitle, card.speaker.sourceDate);
    case "insight":
      return withDate(card.insight.sourceTitle, card.insight.sourceDate);
    case "contradiction":
      return card.contradiction.origin === "challenge" ? "found while synthesizing" : "found by the sweep";
    case "question":
      return card.question.sourceTitle ?? "noticed in your world view";
  }
}

function KindBadge({ kind }: { kind: NeedsYouKind }) {
  const meta = COPY[kind];

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] px-2.5 py-1 text-[11px] font-semibold"
      style={{ background: `color-mix(in oklch, ${meta.colour} 18%, transparent)`, color: meta.colour }}
    >
      {meta.label}
    </span>
  );
}

function AllClear() {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <CheckCheck size={32} strokeWidth={2} className="text-[var(--success)]" aria-hidden />
      <p className="text-[16px] font-bold text-[var(--text-primary)]">Nothing needs you</p>
      <p className="text-[14px] leading-[1.5] text-[var(--text-muted)]">
        Everything you added is in the world view.
      </p>
    </div>
  );
}

function InsightCard({
  insight,
  onDecided,
  onSkip,
}: {
  insight: ReviewInsight;
  onDecided: (direction: "left" | "right") => void;
  onSkip: () => void;
}) {
  const { api, refreshWorldViewActivity } = useApp();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(insight.normalizedText);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function decide(action: "approve" | "discard") {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      if (action === "approve") {
        const { synthesizing } = await api.approveInsight(insight.id);
        if (synthesizing) refreshWorldViewActivity();
      } else {
        await api.discardInsight(insight.id);
      }
      onDecided(action === "approve" ? "right" : "left");
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved. Try again.");
      setBusy(false);
    }
  }

  /** "Save and add" is what the button says, so it is what it does: improve, then approve. */
  async function saveAndAdd() {
    const text = draft.trim();
    if (busy || text === "") return;
    setBusy(true);
    setProblem(null);
    try {
      await api.improveInsight(insight.id, text);
      const { synthesizing } = await api.approveInsight(insight.id);
      if (synthesizing) refreshWorldViewActivity();
      onDecided("right");
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved. Try again.");
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-2.5">
        <textarea
          value={draft}
          rows={6}
          autoFocus
          aria-label="Improve this insight"
          onChange={(event) => setDraft(event.target.value)}
          className="w-full resize-none rounded-[var(--radius-md)] border border-[var(--primary)] bg-[var(--surface-2)] p-3 text-[17px] leading-[1.5] text-[var(--text-primary)] outline-none"
        />
        <p className="text-[12px] text-[var(--text-muted)] [text-wrap:pretty]">
          Saving rewrites it in your own words and adds it to your world view.
        </p>
        <Problem text={problem} />
        <PrimaryButton disabled={busy || draft.trim() === ""} onClick={() => void saveAndAdd()}>
          {busy ? "Saving…" : "Save and add"}
        </PrimaryButton>
        <QuietButton
          onClick={() => {
            setDraft(insight.normalizedText);
            setEditing(false);
          }}
        >
          Cancel
        </QuietButton>
      </div>
    );
  }

  return (
    <>
      <p className="text-[18px] leading-[1.5] text-[var(--text-primary)] [text-wrap:pretty]">
        {insight.normalizedText}
      </p>

      {/* INS-1 — the extractor could not name something this sentence leans on. Every other
          surface says the same thing at the same moment; approving it anyway is how a belief
          about nothing in particular gets into the world view. */}
      {!insight.grounded && (
        <p className="rounded-[var(--radius-md)] border border-[var(--warning)] bg-[color-mix(in_oklch,var(--warning)_12%,transparent)] p-3 text-[13px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
          {describeUngrounded(insight.ungroundedReasons) ||
            "Something this refers to could not be named from the source."}{" "}
          Improve it before adding, or it will be believed about nothing in particular.
        </p>
      )}

      <Problem text={problem} />

      <div className="mt-auto flex flex-col gap-2 pt-2.5">
        <PrimaryButton disabled={busy} onClick={() => void decide("approve")}>
          {busy ? "Saving…" : "Add to world view"}
        </PrimaryButton>
        <OutlineButton disabled={busy} onClick={() => setEditing(true)}>
          <PencilLine size={16} strokeWidth={2} aria-hidden />
          Improve
        </OutlineButton>
        <QuietButton disabled={busy} onClick={() => void decide("discard")}>
          Not true
        </QuietButton>
        <LaterButton onClick={onSkip} />
      </div>
    </>
  );
}

/** SPCH-8 on a phone: what was heard, what it should say, and why. */
function CorrectionCard({
  correction,
  onDecided,
  onSkip,
}: {
  correction: PendingCorrection;
  onDecided: (direction: "left" | "right") => void;
  onSkip: () => void;
}) {
  const { api } = useApp();

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function decide(apply: boolean) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.resolveProposal(correction.sourceMaterialId, correction.id, apply);
      onDecided(apply ? "right" : "left");
    } catch (cause: unknown) {
      setProblem(
        cause instanceof ApiError && cause.status === 404
          ? "That has already been answered somewhere else."
          : "That could not be saved. Try again.",
      );
      setBusy(false);
    }
  }

  return (
    <>
      <p className="text-[16px] leading-[1.5] text-[var(--text-muted)] line-through">{correction.originalText}</p>
      <p className="text-[17px] leading-[1.5] text-[var(--vl-life-soft)] [text-wrap:pretty]">
        {correction.correctedText}
      </p>
      {correction.reasoning && (
        <p className="text-[13px] italic text-[var(--text-muted)] [text-wrap:pretty]">{correction.reasoning}</p>
      )}

      <Problem text={problem} />

      <div className="mt-auto flex flex-col gap-2 pt-2.5">
        <PrimaryButton disabled={busy} onClick={() => void decide(true)}>
          {busy ? "Saving…" : "Apply the fix"}
        </PrimaryButton>
        <QuietButton disabled={busy} onClick={() => void decide(false)}>
          Keep the original
        </QuietButton>
        <LaterButton onClick={onSkip} />
      </div>
    </>
  );
}

/**
 * AUTO-1 on a phone. The two beliefs are drawn identically and in full, because the screen
 * must not imply which one is right — the one thing that must not be lost in the narrowing.
 *
 * Three of the desktop's four answers are here: keep A, keep B, and acknowledge both.
 * Discarding the pair is not, and "Leave for now" is the handoff's own word for leaving it —
 * so this card has no separate "Decide later", which would be the same button twice.
 */
function ContradictionCard({
  card,
  onAnswered,
  onSkip,
}: {
  card: Contradiction;
  onAnswered: () => void;
  onSkip: () => void;
}) {
  const { api } = useApp();

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function answer(action: "acknowledge" | "keep", keepBeliefId?: string) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.resolveContradiction(card.id, action, keepBeliefId);
      onAnswered();
    } catch (cause: unknown) {
      setProblem(
        cause instanceof ApiError && cause.status === 404
          ? "That has already been answered somewhere else."
          : "That could not be saved. Try again.",
      );
      setBusy(false);
    }
  }

  return (
    <>
      <p className="text-[16px] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
        {card.subjectTitle}
      </p>

      <div className="flex flex-col gap-2.5">
        {[
          { label: "One side", belief: card.beliefA },
          { label: "The other", belief: card.beliefB },
        ].map(({ label, belief }) => (
          <div
            key={belief.id}
            className="flex flex-col gap-2 rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-2)] p-3.5"
          >
            <span className="text-[11px] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              {label}
            </span>

            <p className="text-[16px] leading-[1.5] text-[var(--text-primary)] [text-wrap:pretty]">
              {belief.statement}
            </p>

            <p className="text-[12px] text-[var(--text-muted)]">
              {withDate(belief.sourceTitle, belief.sourceDate)}
            </p>

            <button
              type="button"
              disabled={busy}
              onClick={() => void answer("keep", belief.id)}
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--border-default)] text-[14px] font-semibold text-[var(--text-primary)] disabled:opacity-50"
            >
              Keep this one
            </button>
          </div>
        ))}
      </div>

      <p className="text-[12px] leading-[1.5] text-[var(--text-muted)] [text-wrap:pretty]">
        Keeping both leaves both standing — some disagreements are real in the world.
      </p>

      <Problem text={problem} />

      <div className="mt-auto flex flex-col gap-2 pt-2.5">
        <PrimaryButton disabled={busy} onClick={() => void answer("acknowledge")}>
          {busy ? "Saving…" : "Both are true"}
        </PrimaryButton>
        <QuietButton disabled={busy} onClick={onSkip}>
          Leave for now
        </QuietButton>
      </div>
    </>
  );
}

/**
 * SPCH-6 on a phone — the handoff's "Who is this?" card.
 *
 * **The fragment player is here although the handoff's phone drawing does not show one.**
 * That is the one deliberate addition on this screen, and the reason is the rule the whole
 * increment turns on: a reduced surface must not be a way to decide something with less
 * information than a wider one shows. A voice cannot be recognised from a transcript line —
 * the desktop card offers six seconds of it, and a phone card without them would be asking
 * somebody to name a person by reading what they said.
 */
function SpeakerCard({
  speaker,
  onNamed,
  onSkip,
}: {
  speaker: UnnamedSpeaker;
  onNamed: () => void;
  onSkip: () => void;
}) {
  const { api, entities } = useApp();

  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const persons = useMemo(() => entities.filter((entity) => entity.type === "person"), [entities]);
  const player = useAudioFragment(api, speaker.sourceMaterialId);

  function name(who: { personId: string } | { name: string }) {
    if (busy) return;
    setBusy(true);
    setProblem(null);

    void api
      .confirmSpeaker(speaker.sourceMaterialId, speaker.id, who)
      .then(() => {
        player.stop();
        onNamed();
      })
      .catch((cause: unknown) => {
        setProblem(
          cause instanceof ApiError && cause.status === 404
            ? "That voice has already been named somewhere else."
            : "That could not be saved. Try again.",
        );
        setBusy(false);
      });
  }

  return (
    <>
      <p className="text-[14px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
        {speaker.matchedPersonId
          ? "One voice in this recording is close to somebody Factorium already knows, but not close enough to be sure."
          : "One voice in this recording does not match anyone Factorium knows well enough."}
      </p>

      {speaker.sampleText && (
        <p className="text-[17px] leading-[1.5] italic text-[var(--text-primary)] [text-wrap:pretty]">
          “{speaker.sampleText}”
        </p>
      )}

      {speaker.sampleStartMs !== null && (
        <FragmentPlayer
          startMs={speaker.sampleStartMs}
          endMs={speaker.sampleEndMs}
          playing={player.playing === speaker.id}
          progress={player.playing === speaker.id ? player.progress : 0}
          disabled={player.problem !== null}
          onToggle={() =>
            player.play({ id: speaker.id, startMs: speaker.sampleStartMs!, endMs: speaker.sampleEndMs })
          }
        />
      )}

      <Problem text={player.problem} />

      {picking ? (
        <NamePicker
          heading="Who is this?"
          persons={persons}
          busy={busy}
          onPick={name}
          onCancel={() => setPicking(false)}
        />
      ) : (
        speaker.matchedPersonId &&
        speaker.matchedPersonName && (
          <button
            type="button"
            disabled={busy}
            onClick={() => name({ personId: speaker.matchedPersonId! })}
            className="flex items-center gap-3 rounded-[var(--radius-lg)] border border-[var(--border-subtle)] p-3 text-left disabled:opacity-50"
          >
            <span
              aria-hidden
              className="flex size-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-[var(--text-inverse)]"
              style={{ background: personColour(speaker.matchedPersonId) }}
            >
              {personInitials(speaker.matchedPersonName)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[16px] font-semibold text-[var(--text-primary)]">
                {speaker.matchedPersonName}
              </span>
              <span className="text-[12px] text-[var(--text-muted)]">Heard before in your recordings</span>
            </span>
            {speaker.matchScore !== null && (
              <span className="shrink-0 font-[family-name:var(--font-mono)] text-[12px] text-[var(--text-muted)]">
                {speaker.matchScore.toFixed(2)} match
              </span>
            )}
          </button>
        )
      )}

      <Problem text={problem} />

      <div className="mt-auto flex flex-col gap-2 pt-2.5">
        {!picking && (
          <QuietButton disabled={busy} onClick={() => setPicking(true)}>
            {speaker.matchedPersonId ? "Someone else" : "Say who it is"}
          </QuietButton>
        )}
        <LaterButton onClick={onSkip} />
      </div>
    </>
  );
}

function Problem({ text }: { text: string | null }) {
  if (!text) return null;

  return (
    <p role="alert" className="text-[13px] text-[var(--warning)]">
      {text}
    </p>
  );
}

/** 52 px, full width — the handoff's thumb-sized primary. */
function PrimaryButton({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="h-[52px] w-full rounded-[var(--radius-md)] bg-[var(--primary)] text-[16px] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function OutlineButton({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-default)] text-[16px] font-semibold text-[var(--text-primary)] disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function QuietButton({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="h-[52px] w-full rounded-[var(--radius-md)] text-[16px] text-[var(--text-muted)] disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function LaterButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="h-11 w-full text-[13px] text-[var(--text-muted)]">
      Decide later
    </button>
  );
}
